import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom';

import { CrmDetailSkeleton } from './components/crm/CrmSkeleton';

import { RequireAuth } from './components/auth/RequireAuth';
import { CrmShell } from './components/crm/CrmShell';
import { accountRoutes } from './module/account/routes';
import { billingRoutes } from './module/billing/routes';
import { paymentRoutes } from './module/payment/routes';
import { brokerRoutes } from './module/broker/routes';
import { helpRoutes } from './module/help/routes';
import { marketOpsRoutes } from './module/marketops/routes';
import { meteringRoutes } from './module/metering/routes';
import { notificationRoutes } from './module/notification/routes';
import { onboardingRoutes } from './module/onboarding/routes';
import { pricingRoutes } from './module/pricing/routes';
import { prospectsRoutes } from './module/prospects/routes';
import { quotationRoutes } from './module/quotation/routes';
import { reportsRoutes } from './module/reports/routes';
import { tariffsRoutes } from './module/tariffs/routes';
import { usersRoutes } from './module/users/routes';

const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const CallbackPage = lazy(() =>
  import('./pages/CallbackPage').then((m) => ({ default: m.CallbackPage })),
);
const UnderDevelopmentPage = lazy(() =>
  import('./pages/UnderDevelopmentPage').then((m) => ({ default: m.UnderDevelopmentPage })),
);
const ActivatePage = lazy(() =>
  import('./module/users/pages/ActivatePage').then((m) => ({ default: m.ActivatePage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const DocsPortalPage = lazy(() =>
  import('./pages/DocsPortalPage').then((m) => ({ default: m.DocsPortalPage })),
);
/**
 * Settings is an APP-LEVEL page, so its route is declared here rather than
 * exported from `module/settings`.
 *
 * The module used to own `/settings/templates`, which was right while Templates
 * WAS the whole of Settings. The page now composes app-level chrome (Profile /
 * Preferences / Sign out) with content from TWO country modules, and only an
 * app-level page may know all three — a module route pointing at `src/pages/`
 * would invert the dependency direction the folder rules set.
 * `module/settings` keeps owning the Templates behaviour; this owns the door.
 */
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

/**
 * Root layout — a SINGLE Suspense boundary around every route. The page
 * components are `React.lazy`, so navigating to a route whose chunk is not
 * cached yet causes that element to SUSPEND. Without a Suspense ancestor React
 * throws "A component suspended while responding to synchronous input" instead
 * of showing a fallback. This bit on logout once sessions persisted: the
 * session rehydrates from storage without ever loading the LoginPage chunk, so
 * logging out triggers a fresh lazy load of /login that had nowhere to suspend.
 * This boundary gives every lazy route (login, activate, callback, and all the
 * shell pages) a fallback to render while its chunk loads.
 */
function RootLayout(): JSX.Element {
  return (
    <Suspense
      fallback={
        /* No accessible NAME here previously — role="status" on a wrapper with
           a label-less Spinner announces an empty live region, so a screen
           reader got silence. The skeleton names itself. */
        <div className="mx-auto w-full max-w-5xl px-6 py-10">
          <CrmDetailSkeleton label="Loading" />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  );
}

/**
 * THE ROUTE TABLE THE APP ACTUALLY SERVES.
 *
 * Exported as data, and `createBrowserRouter` below is constructed from THIS
 * array — it is not a second copy. Route tests drive `usRoutes` through a
 * `createMemoryRouter`, so a test can only make a routing claim the running
 * application would honour.
 *
 * WHY THIS IS EXPORTED AT ALL: `docs/defect-classes/test-route-drift.md`. A
 * test that mounts a page component at a path the router does not serve passes,
 * proves nothing, and reports coverage of a journey no user can reach. The only
 * defence that does not itself drift is deriving the table from the same array
 * the router receives. Never restate these routes anywhere — import them.
 */
export const usRoutes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      // Auth screens render full-screen, no shell. /login and /auth/callback are
      // the only unguarded routes. /auth/callback is the OIDC redirect landing —
      // it must stay outside RequireAuth so the in-flight code exchange is not
      // bounced to /login before <AuthProvider> completes it.
      { path: '/login', element: <LoginPage /> },
      { path: '/auth/callback', element: <CallbackPage /> },
      // Public invite-activation page (set password → OTP). Reached from the
      // invite email link; must stay outside RequireAuth (the user isn't logged in).
      { path: '/activate', element: <ActivatePage /> },
      // Public self-service password reset. Both MUST stay outside RequireAuth:
      // the whole point is that the user cannot sign in. `/reset-password` is
      // the landing path Accounts builds into the emailed link
      // (`passwordResetLinkPrefix`) — if this path ever changes, that prefix in
      // `server.ts` has to change with it or every reset email 404s.
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },

      // Platform documentation portal — authenticated but rendered full-screen
      // (its own layout + an <iframe> of the synced static docs site), so it sits
      // OUTSIDE the CrmShell to avoid a double sidebar. Role-gating (admin/manager)
      // is enforced inside DocsPortalPage.
      {
        path: '/docs',
        element: (
          <RequireAuth>
            <DocsPortalPage />
          </RequireAuth>
        ),
      },

      // Everything else lives under the CRM shell AND requires authentication.
      // RequireAuth wraps CrmShell so a single guard covers every nested route.
      {
        element: (
          <RequireAuth>
            <CrmShell />
          </RequireAuth>
        ),
        children: [
          { path: '/', element: <Navigate to="/dashboard" replace /> },
          { path: '/dashboard', element: <HomePage /> },
          ...billingRoutes,
          ...paymentRoutes,
          ...quotationRoutes,
          ...accountRoutes,
          ...brokerRoutes,
          ...prospectsRoutes,
          ...onboardingRoutes,
          ...notificationRoutes,
          ...pricingRoutes,
          ...tariffsRoutes,
          ...usersRoutes,
          ...meteringRoutes,
          ...marketOpsRoutes,
          ...reportsRoutes,
          { path: '/settings', element: <SettingsPage /> },
          // The deep link E-DT04 shipped, kept working. It is in operators'
          // history and in the epic doc, so it redirects onto the same page with
          // the tab preselected rather than 404-ing.
          {
            path: '/settings/templates',
            element: <Navigate to="/settings?tab=templates" replace />,
          },
          ...helpRoutes,
          { path: '*', element: <UnderDevelopmentPage /> },
        ],
      },
    ],
  },
];

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(usRoutes);
